/*
 * Copyright (c) 2019. ProVision Media Group Ltd. <https://provision.bg>
 * Venelin Iliev <http://veneliniliev.com>
 */

import { Component, Input, OnInit } from '@angular/core';
import { Observable, of, Subject } from 'rxjs';
import { ApiQuery, GeoFenceFilter } from '@app/core/http/api-query';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { PaginatedResponse } from '@app/shared/models/paginated-response.model';
import { EventsService } from '@app/events/events/events.service';
import { Events } from '@app/events/events/events.model';
import { catchError, debounceTime, distinctUntilChanged, switchMap, tap } from 'rxjs/operators';
import { EventsFormService } from '@app/events/events/events-form.service';
import { Logger } from '@app/core';
import { environment } from '@env/environment';
import { DataSet } from '@app/shared/kendo/data-set';
import * as moment from 'moment';

const log = new Logger('EventsSelectComponent');

@Component({
  selector: 'app-events-select',
  templateUrl: './events-select.component.html',
  styleUrls: ['./events-select.component.scss'],
})
export class EventsSelectComponent implements OnInit {
  data: Observable<Events[]>;
  apiQuery: ApiQuery = new ApiQuery();
  loader: boolean;
  @Input() FormControl: FormControl;
  form: FormGroup;
  input$ = new Subject<string>();
  @Input() required = false;
  env = environment;
  @Input() bookingMode = false;
  lastResponse: any = null;
  @Input() isInModal = true;

  constructor(
    private eventService: EventsService,
    private formBuilder: FormBuilder,
    private eventFormService: EventsFormService
  ) {}

  ngOnInit() {
    /*
     *  създава fake formGroup, за да може да пази временно стойностите някъде
     *  после чрез евентите add, remove и clear ги прехвърля към подадения на компонента контрол
     */
    if (this.required) {
      this.form = this.formBuilder.group({
        events_fake: [[], [Validators.required]],
      });
    } else {
      this.form = this.formBuilder.group({
        events_fake: [[]],
      });
    }

    /*
     * това помага за patch-a на parent формата
     * ако се пали в някой друг случай трябва да се измисли нещо по-добро
     */
    this.FormControl.valueChanges.subscribe((values: []) => {
      setTimeout(() => {
        /*
         *  Проверява дали всички обекти имат name
         *  Ако някой няма, то не е подаден обект от тип Events или не е пълен
         *  В такъв случай гетва на ново през сървиса всеки Event
         */
        if (values.some((x) => !x['name'])) {
          this.loadEventsByIds(values);
        } else {
          /*
           * Цялата тая въртележка е само за да може да си позне типа и да извика "formattedName" в темплейта
           */
          const temp: Array<Events> = new Array<Events>();
          values.forEach((event: Events) => {
            temp.push(new Events().deserialize(event));
          });
          this.data = of(temp);
        }

        const preSelectedValues: Array<any> = [];
        values.forEach((event: any) => {
          preSelectedValues.push(event.id);
        });
        this.form.get('events_fake').setValue(preSelectedValues);
      });
    });

    this.eventsSearch();
  }

  // loadEvents(keyword?: string) {
  //   this.loader = true;
  //
  //   this.eventService
  //     .index(this.apiQuery.addIncludes('venue.city.country').addFilter('name', keyword))
  //     .subscribe((response: PaginatedResponse) => {
  //       this.data = of(response.data);
  //       this.loader = false;
  //     });
  // }

  loadEventById(id: number) {
    return this.eventService.show(id);
  }

  loadEventsByIds(events: any[]) {
    this.loader = true;
    for (let i = 0; i < events.length; i++) {
      this.eventService.show(events[i]['id']).subscribe((ev: Events) => {
        this.data = of([ev]); // не знам защо работи
      });
    }
    this.loader = false;
    // return result;
  }

  eventsSearch() {
    this.input$
      .pipe(
        tap(() => (this.loader = true)),
        debounceTime(1000),
        distinctUntilChanged(),
        switchMap((term: any) => {
          if (this.bookingMode) {
            if (this.FormControl.value[0]) {
              // добавя филтър за +- дни date_from от първия евент
              const offset = 3, // days
                radius = 100, // km
                dataSet = new DataSet()
                  .addFilter(
                    'date_from',
                    moment(this.FormControl.value[0].date_from)
                      .subtract(offset, 'days')
                      .format(environment.apiPipeDateFormat),
                    'gte'
                  )
                  .addFilter(
                    'date_from',
                    moment(this.FormControl.value[0].date_from)
                      .add(offset, 'days')
                      .format(environment.apiPipeDateFormat),
                    'lte',
                    true,
                    true
                  );

              // добавя geofence филтър към query-то
              const geoFenceFilter = new GeoFenceFilter();
              geoFenceFilter.lat = Number(this.FormControl.value[0].lat);
              geoFenceFilter.long = Number(this.FormControl.value[0].long);
              geoFenceFilter.inner_radius = 0;
              geoFenceFilter.outer_radius = radius;

              this.apiQuery.setDataSetFilters(dataSet).setGeoFence(geoFenceFilter);
            } else {
              // ако няма избран първи евент премахва geofence и date_from филтрите
              this.apiQuery.setDataSetFilters(null).removeGeoFence();
            }
          }

          // За да ме прави излишен request след като се избере нещо
          if (!term) {
            return of(this.lastResponse);
          }

          return this.eventService
            .index(
              this.apiQuery
                .addFilters({
                  active: true,
                  name: term,
                })
                .addIncludes('venue.city.country')
            )
            .pipe(
              catchError(() => of([])), // empty list on error
              tap(() => (this.loader = false))
            );
        })
      )
      .subscribe((events: PaginatedResponse) => {
        this.data = of(events.data);
        this.lastResponse = events;
        this.loader = false;
      });
  }

  add(event: any) {
    this.FormControl.value.push(event);
    this.FormControl.updateValueAndValidity();
  }

  remove(id: any) {
    let index: number;
    this.FormControl.value.forEach((value: any, ind: number) => {
      if (value.id === id) {
        index = ind;
      }
    });
    this.FormControl.value.splice(index, 1);
    this.FormControl.updateValueAndValidity();
  }

  clear() {
    this.FormControl.setValue([]);
    this.FormControl.updateValueAndValidity();
  }

  addNew(searchTerm: string) {
    this.eventFormService.open(new Events().deserialize({ name: searchTerm })).then(
      (response) => {
        log.debug('response', response);
        if (!response.active) {
          return log.warn('Try to add non active event!');
        }
        const newValues = [...this.form.get('events_fake').value, response];
        log.debug('newValues', newValues);

        this.form.get('events_fake').setValue(newValues);
        this.add(response);
      },
      (err) => {
        log.error(err);
      }
    );
  }
}
